import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AppLayout from '../../components/Layout/AppLayout';
import Card from '../../components/UI/Card';
import InfoTooltip from '../../components/UI/InfoTooltip';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/supabase';
import { formatCurrency } from '../../utils/currency';
import { buildActivityReportLink } from '../../utils/activityReportLink';
import '../../styles/brand-typography.css';
import {
  buildUnifiedActivity,
  filterActivityByDateRange,
  getActivityReportSummary,
  getGoalBalances,
  getLocalDateKey,
  getRollingDateRange,
} from '../../utils/activity';

const formatReportDate = (dateKey) => {
  if (!dateKey) return '';

  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getThisYearRange = () => {
  const today = new Date();

  return {
    startDate: `${today.getFullYear()}-01-01`,
    endDate: getLocalDateKey(today),
  };
};

const calculateCurrentPiggyTotal = (transactions = []) => {
  return (transactions || []).reduce((total, transaction) => {
    if (transaction.approval_status !== 'approved') return total;

    const amount = Number(transaction.amount_cents) || 0;

    if (transaction.type === 'income') return total + amount;
    if (transaction.type === 'expense') return total - amount;

    return total;
  }, 0);
};



const getIncomeSourceKey = (activity) => {
  if (activity?.category === 'Chores') return 'chores';

  const subtype = String(activity?.subtype || '').toLowerCase();

  if (subtype === 'allowance') return 'allowance';
  if (subtype === 'gift') return 'gift';
  if (subtype === 'bonus') return 'bonus';

  return 'other';
};

const DonutChart = ({ totalCents, segments, emptyLabel = 'No activity in this period' }) => {
  const nonZeroSegments = segments.filter((segment) => segment.valueCents > 0);

  let runningPercent = 0;
  const stops = nonZeroSegments.map((segment) => {
    const start = runningPercent;
    const percent = totalCents > 0 ? (segment.valueCents / totalCents) * 100 : 0;
    runningPercent += percent;
    return `${segment.color} ${start}% ${runningPercent}%`;
  });

  const background =
    totalCents > 0 && stops.length > 0
      ? `conic-gradient(${stops.join(', ')})`
      : 'conic-gradient(#cbd5e1 0% 100%)';

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex justify-center flex-shrink-0">
        <div
          className="relative h-28 w-28 rounded-full"
          style={{ background }}
          role="img"
          aria-label={
            totalCents > 0
              ? `Chart total ${formatCurrency(totalCents)}`
              : emptyLabel
          }
        >
          <div className="absolute inset-4 flex items-center justify-center rounded-full bg-white dark:bg-slate-800 shadow-inner">
            <div className="text-center px-2">
              <div className="text-xs font-semibold text-gray-500 dark:text-slate-400">
                Total
              </div>
              <div className="mt-0.5 break-words text-xs font-extrabold leading-tight text-gray-900 dark:text-white">
                {formatCurrency(totalCents)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full min-w-0 flex-1 space-y-2">
        {segments.map((segment) => {
          const percent =
            totalCents > 0 ? Math.round((segment.valueCents / totalCents) * 100) : 0;

          return (
            <div
              key={segment.key}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: segment.color }}
                  aria-hidden="true"
                />
                <span className="text-xs leading-snug text-gray-700 dark:text-slate-300 break-words">
                  {segment.label}
                </span>
              </div>

              <div className="text-right">
                <span className="text-xs font-bold text-gray-900 dark:text-slate-100">
                  {formatCurrency(segment.valueCents)}
                </span>
                <span className="ml-1 text-[11px] text-gray-500 dark:text-slate-400">
                  {percent}%
                </span>
              </div>
            </div>
          );
        })}

        {totalCents === 0 ? (
          <p className="pt-1 text-sm text-gray-500 dark:text-slate-400">
            {emptyLabel}
          </p>
        ) : null}
      </div>
    </div>
  );
};

const SummaryCard = ({ icon, label, value, tooltip, accentClass }) => (
  <Card className={`p-3 last:col-span-2 sm:last:col-span-1 ${accentClass || ''}`}>
    <div className="flex items-start gap-1.5">
      <MyPiggyIcon name={icon} size="xs" />
      <span className="min-w-0 flex-1 text-xs font-extrabold leading-snug text-gray-700 dark:text-slate-200">{label}</span>
      <InfoTooltip compact label={`About ${label}`}>{tooltip}</InfoTooltip>
    </div>
    <div className="mt-2 break-words text-xl font-extrabold text-gray-900 dark:text-white">{value}</div>
  </Card>
);

export default function ParentReports() {
  const navigate = useNavigate();
  const { parent } = useAuth();

  const initialRange = getRollingDateRange(30);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [children, setChildren] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [savingsLedger, setSavingsLedger] = useState([]);
  const [goals, setGoals] = useState([]);
  const [allActivity, setAllActivity] = useState([]);

  const [childFilter, setChildFilter] = useState('all');
  const [period, setPeriod] = useState('last30');
  const [reportRange, setReportRange] = useState(initialRange);
  const [customStart, setCustomStart] = useState(initialRange.startDate);
  const [customEnd, setCustomEnd] = useState(initialRange.endDate);
  const [customError, setCustomError] = useState('');

  useEffect(() => {
    document.title = 'Family Reports | MyPiggy';

    return () => {
      document.title = 'MyPiggy';
    };
  }, []);

  useEffect(() => {
    const loadReports = async () => {
      if (!parent?.family_id) return;

      try {
        setLoading(true);
        setError('');

        const kids = await db.getChildren(parent.family_id);

        const [txns, ledger, goalGroups] = await Promise.all([
          db.getTransactions(parent.family_id, null, { allPages: true }),
          db.getSavingsLedgerByFamily(parent.family_id, { allPages: true }),
          Promise.all((kids || []).map((kid) => db.getSavingsGoals(kid.id, { allPages: true }))),
        ]);

        const goals = goalGroups.flat();

        setChildren(kids || []);
        setTransactions(txns || []);
        setSavingsLedger(ledger || []);
        setGoals(goals || []);
        setAllActivity(
          buildUnifiedActivity({
            transactions: txns || [],
            savingsLedger: ledger || [],
            goals,
          })
        );
      } catch (loadError) {
        console.error('Unable to load family reports:', loadError);
        setError(loadError?.message || 'Unable to load family reports. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [parent?.family_id]);

  const selectedChild = useMemo(
    () => children.find((child) => String(child.id) === String(childFilter)) || null,
    [children, childFilter]
  );

  const selectPeriod = (nextPeriod) => {
    setPeriod(nextPeriod);
    setCustomError('');

    if (nextPeriod === 'last7') {
      setReportRange(getRollingDateRange(7));
    } else if (nextPeriod === 'last30') {
      setReportRange(getRollingDateRange(30));
    } else if (nextPeriod === 'last90') {
      setReportRange(getRollingDateRange(90));
    } else if (nextPeriod === 'year') {
      setReportRange(getThisYearRange());
    }
  };

  const applyCustomRange = () => {
    setCustomError('');

    if (!customStart || !customEnd) {
      setCustomError('Choose both a start date and an end date.');
      return;
    }

    if (customStart > customEnd) {
      setCustomError('The start date must be before the end date.');
      return;
    }

    setReportRange({
      startDate: customStart,
      endDate: customEnd,
    });
  };

  const childActivity = useMemo(() => {
    if (childFilter === 'all') return allActivity;

    return allActivity.filter(
      (activity) => String(activity.child_id) === String(childFilter)
    );
  }, [allActivity, childFilter]);

  const reportActivity = useMemo(
    () =>
      filterActivityByDateRange(
        childActivity,
        reportRange.startDate,
        reportRange.endDate
      ),
    [childActivity, reportRange.startDate, reportRange.endDate]
  );

  const summary = useMemo(
    () => getActivityReportSummary(reportActivity),
    [reportActivity]
  );

  const selectedTransactions = useMemo(() => {
    if (childFilter === 'all') {
      const activeIds = new Set(children.map((child) => String(child.id)));
      return transactions.filter((transaction) => activeIds.has(String(transaction.child_id)));
    }

    return transactions.filter(
      (transaction) => String(transaction.child_id) === String(childFilter)
    );
  }, [transactions, children, childFilter]);

  const currentPiggyTotal = useMemo(
    () => calculateCurrentPiggyTotal(selectedTransactions),
    [selectedTransactions]
  );

  const childNameMap = useMemo(
    () => new Map(children.map((child) => [
      String(child.id),
      `${child.nickname || 'Kid'}${
        child.status === 'archived'
          ? ' (Deactivated)'
          : ''
      }`,
    ])),
    [children]
  );

  const incomeBreakdown = useMemo(() => {
    const totals = {
      allowance: 0,
      chores: 0,
      gift: 0,
      bonus: 0,
      other: 0,
    };

    reportActivity.forEach((activity) => {
      if (
        activity.activity_kind !== 'transaction' ||
        activity.type !== 'income' ||
        activity.approval_status !== 'approved'
      ) {
        return;
      }

      const key = getIncomeSourceKey(activity);
      totals[key] += Number(activity.amount_cents) || 0;
    });

    return totals;
  }, [reportActivity]);

  const incomeSegments = useMemo(
    () => [
      { key: 'allowance', label: 'Allowance', valueCents: incomeBreakdown.allowance, color: '#10b981' },
      { key: 'chores', label: 'Chores', valueCents: incomeBreakdown.chores, color: '#f59e0b' },
      { key: 'gift', label: 'Gifts', valueCents: incomeBreakdown.gift, color: '#8b5cf6' },
      { key: 'bonus', label: 'Bonus', valueCents: incomeBreakdown.bonus, color: '#3b82f6' },
      { key: 'other', label: 'Other', valueCents: incomeBreakdown.other, color: '#64748b' },
    ],
    [incomeBreakdown]
  );

  const usedTotalCents = summary.moneySpentCents + summary.movedToGoalsCents;
  const moneyUsedSegments = useMemo(
    () => [
      { key: 'spent', label: 'Spent', valueCents: summary.moneySpentCents, color: '#ef4444' },
      { key: 'goals', label: 'Added to Savings Goals', valueCents: summary.movedToGoalsCents, color: '#3b82f6' },
    ],
    [summary.moneySpentCents, summary.movedToGoalsCents]
  );

  const goalBalances = useMemo(
    () => getGoalBalances(goals, savingsLedger),
    [goals, savingsLedger]
  );

  const visibleGoals = useMemo(() => {
    const activeChildIds = new Set(children.map((child) => String(child.id)));

    return goals
      .filter((goal) => {
        if (goal.status !== 'active') return false;
        if (!activeChildIds.has(String(goal.child_id))) return false;
        if (childFilter === 'all') return true;
        return String(goal.child_id) === String(childFilter);
      })
      .map((goal) => ({
        ...goal,
        balanceCents: Math.max(0, goalBalances.get(goal.id) || 0),
        childName: childNameMap.get(String(goal.child_id)) || 'Kid',
      }));
  }, [goals, children, childFilter, goalBalances, childNameMap]);


  // --------------------------------------------------
  // Money Habits insights
  // --------------------------------------------------
  // These are descriptive observations only. We intentionally avoid
  // grading children, comparing siblings, or labeling a savings rate as
  // good/bad. The goal is to give parents useful conversation starters.

  const moneyHabits = useMemo(() => {
    const moneyAddedCents = Number(summary.moneyAddedCents) || 0;
    const movedToGoalsCents = Number(summary.movedToGoalsCents) || 0;
    const choreEarningsCents = Number(summary.choreEarningsCents) || 0;

    const savingsPercent =
      moneyAddedCents > 0
        ? Math.round((movedToGoalsCents / moneyAddedCents) * 100)
        : null;

    const approvedChoreCount = reportActivity.filter(
      (activity) =>
        activity.activity_kind === 'transaction' &&
        activity.type === 'income' &&
        activity.category === 'Chores' &&
        activity.approval_status === 'approved'
    ).length;

    const visibleGoalIds = new Set(visibleGoals.map((goal) => String(goal.id)));
    const contributedGoalIds = new Set();

    reportActivity.forEach((activity) => {
      if (activity.activity_kind !== 'savings') return;

      // A direct deposit means that goal received money during the
      // selected period.
      if (
        activity.savings_activity_type === 'deposit' &&
        activity.goal_id &&
        visibleGoalIds.has(String(activity.goal_id))
      ) {
        contributedGoalIds.add(String(activity.goal_id));
      }

      // A goal-to-goal transfer also represents progress for the
      // destination goal. buildUnifiedActivity keeps the destination
      // goal id in related_goal_id on the visible transfer activity.
      // We intentionally do NOT count the source goal again here: the
      // source is losing money, while the destination is receiving it.
      if (
        activity.savings_activity_type === 'transfer' &&
        activity.related_goal_id &&
        visibleGoalIds.has(String(activity.related_goal_id))
      ) {
        contributedGoalIds.add(String(activity.related_goal_id));
      }
    });

    return {
      savingsPercent,
      choreEarningsCents,
      approvedChoreCount,
      activeGoalCount: visibleGoals.length,
      contributedGoalCount: contributedGoalIds.size,
    };
  }, [summary, reportActivity, visibleGoals]);

  const moneyHabitCopy = useMemo(() => {
    const isFamily = childFilter === 'all';
    const childName = selectedChild?.nickname || 'Your child';

    let savingsText;
    if (moneyHabits.savingsPercent === null) {
      savingsText = isFamily
        ? "No new money was added during this period, so a family savings percentage isn't available."
        : `No new money was added for ${childName} during this period, so a savings percentage isn't available.`;
    } else if (summary.movedToGoalsCents <= 0) {
      savingsText = isFamily
        ? 'No money was directed toward savings goals during this period.'
        : `${childName} did not move money toward savings goals during this period.`;
    } else {
      savingsText = isFamily
        ? `Savings contributions were ${moneyHabits.savingsPercent}% of Money Added during this period.`
        : `${childName}'s savings contributions were ${moneyHabits.savingsPercent}% of Money Added during this period.`;
    }

    let choreText;
    if (moneyHabits.approvedChoreCount === 0) {
      choreText = isFamily
        ? 'No approved chore earnings were recorded during this period.'
        : `No approved chore earnings were recorded for ${childName} during this period.`;
    } else {
      const choreWord = moneyHabits.approvedChoreCount === 1 ? 'chore' : 'chores';
      choreText = isFamily
        ? `Children earned ${formatCurrency(moneyHabits.choreEarningsCents)} from ${moneyHabits.approvedChoreCount} approved ${choreWord} during this period.`
        : `${childName} earned ${formatCurrency(moneyHabits.choreEarningsCents)} from ${moneyHabits.approvedChoreCount} approved ${choreWord} during this period.`;
    }

    let goalText;
    if (moneyHabits.activeGoalCount === 0) {
      goalText = isFamily
        ? 'There are no active savings goals right now.'
        : `${childName} does not have an active savings goal right now.`;
    } else if (moneyHabits.contributedGoalCount === 0) {
      goalText = isFamily
        ? `No active savings goals received contributions during this period. There ${moneyHabits.activeGoalCount === 1 ? 'is' : 'are'} currently ${moneyHabits.activeGoalCount} active ${moneyHabits.activeGoalCount === 1 ? 'goal' : 'goals'}.`
        : `${childName} did not contribute to an active savings goal during this period. ${childName} currently has ${moneyHabits.activeGoalCount} active ${moneyHabits.activeGoalCount === 1 ? 'goal' : 'goals'}.`;
    } else {
      goalText = isFamily
        ? `Children contributed to ${moneyHabits.contributedGoalCount} of ${moneyHabits.activeGoalCount} active savings ${moneyHabits.activeGoalCount === 1 ? 'goal' : 'goals'} during this period.`
        : `${childName} contributed to ${moneyHabits.contributedGoalCount} of ${moneyHabits.activeGoalCount} active savings ${moneyHabits.activeGoalCount === 1 ? 'goal' : 'goals'} during this period.`;
    }

    return { savingsText, choreText, goalText };
  }, [childFilter, selectedChild, moneyHabits, summary.movedToGoalsCents]);

  const viewTransactions = () => navigate(buildActivityReportLink(childFilter, reportRange), { state: { fromReports: true } });

  return (
    <AppLayout title="Family Reports" className="mypiggy-brand" showBack onBack={() => navigate('/parent')}>
      <div className="space-y-4">
        <Card className="p-4">
          <div className="mb-3 flex items-center gap-2"><MyPiggyIcon name="view-my-reports" size="sm" /><h2 className="text-xl font-extrabold">Family Reports</h2><InfoTooltip label="About Family Reports">See money added, spending, chore earnings and savings contributions for the selected dates. Piggy Bank Total and goal balances are current, regardless of the reporting period.</InfoTooltip></div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><label htmlFor="report-child" className="mb-1 block text-sm font-bold">Child</label><select id="report-child" value={childFilter} onChange={(e) => setChildFilter(e.target.value)} className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 dark:border-slate-600 dark:bg-slate-900"><option value="all">All Kids</option>{children.map((child) => <option key={child.id} value={child.id}>{childNameMap.get(String(child.id))}</option>)}</select></div>
            <div><label htmlFor="report-period" className="mb-1 block text-sm font-bold">Period</label><select id="report-period" value={period} onChange={(e) => selectPeriod(e.target.value)} className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 dark:border-slate-600 dark:bg-slate-900"><option value="last7">Last 7 Days</option><option value="last30">Last 30 Days</option><option value="last90">Last 90 Days</option><option value="year">This Year</option><option value="custom">Custom Range</option></select></div>
          </div>
          {period === 'custom' && <div className="mt-3 rounded-xl bg-gray-50 p-3 dark:bg-slate-900/50"><div className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]"><div><label htmlFor="report-start" className="mb-1 block text-xs font-bold">Start Date</label><input id="report-start" type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900" /></div><div><label htmlFor="report-end" className="mb-1 block text-xs font-bold">End Date</label><input id="report-end" type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900" /></div><button type="button" onClick={applyCustomRange} className="min-h-11 rounded-lg bg-teal-700 px-4 py-2 text-sm font-bold text-white hover:bg-teal-800 focus-visible:ring-2 focus-visible:ring-teal-500">Apply Dates</button></div>{customError && <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">{customError}</p>}</div>}
          <p className="mt-2 text-xs text-gray-600 dark:text-slate-300">Report period: <strong>{formatReportDate(reportRange.startDate)}</strong> – <strong>{formatReportDate(reportRange.endDate)}</strong>.</p>
        </Card>
        {loading ? <Card className="p-6 text-center">Loading report…</Card> : error ? <Card className="p-4"><p role="alert" className="font-semibold text-red-700 dark:text-red-300">{error}</p></Card> : children.length === 0 ? <Card className="p-6 text-center"><p className="font-bold">No children yet.</p><p className="mt-1 text-sm text-gray-600 dark:text-slate-300">Add a child before creating a family report.</p></Card> : <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            <SummaryCard icon="money-received" label="Money Added" value={formatCurrency(summary.moneyAddedCents)} tooltip="Approved income during the selected period. Savings movements are not new income." accentClass="border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20" />
            <SummaryCard icon="spent-money" label="Money Spent" value={formatCurrency(summary.moneySpentCents)} tooltip="Approved expenses during the selected period. Moving money into savings is not spending." accentClass="border-rose-200 bg-rose-50/50 dark:border-rose-800 dark:bg-rose-950/20" />
            <SummaryCard icon="do-chores" label="Chore Earnings" value={formatCurrency(summary.choreEarningsCents)} tooltip="Approved chore rewards during the selected period. These are already included in Money Added." accentClass="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20" />
            <SummaryCard icon="contribute-to-goal" label="Added to Goals" value={formatCurrency(summary.movedToGoalsCents)} tooltip="Money moved from Available Money into savings goals during the selected period. It remains part of Piggy Bank Total." accentClass="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20" />
            <SummaryCard icon="my-piggy-bank" label="Piggy Bank Total" value={formatCurrency(currentPiggyTotal)} tooltip="Current Available Money plus Saved for Goals for the selected child or family. This total is not limited to the report period." accentClass="border-purple-200 bg-purple-50/50 dark:border-purple-800 dark:bg-purple-950/20" />
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-300"><span>Period totals above · Piggy Bank Total is current.</span><InfoTooltip compact label="How these numbers work">Chore Earnings are part of Money Added, not extra money on top of it. Added to Goals is an internal move into savings and does not increase Piggy Bank Total.</InfoTooltip></div>
          <div className="grid gap-3 md:grid-cols-2 md:items-stretch">
            <Card className="flex flex-col p-4"><div className="mb-3 flex items-center gap-2"><h3 className="min-w-0 flex-1 text-sm font-extrabold">Where Money Came From</h3><InfoTooltip compact label="About income sources">Approved Money Added during the selected period, including chore earnings. Savings movements are excluded.</InfoTooltip></div><div className="md:flex md:flex-1 md:items-center"><div className="w-full"><DonutChart totalCents={summary.moneyAddedCents} segments={incomeSegments} emptyLabel="No approved income in this period." /></div></div></Card>
            <Card className="flex flex-col p-4"><div className="mb-3 flex items-center gap-2"><h3 className="min-w-0 flex-1 text-sm font-extrabold">How Money Was Used</h3><InfoTooltip compact label="About money used">Compare approved spending and contributions to savings goals during the selected period. Savings contributions remain inside Piggy Bank Total.</InfoTooltip></div><div className="md:flex md:flex-1 md:items-center"><div className="w-full"><DonutChart totalCents={usedTotalCents} segments={moneyUsedSegments} emptyLabel="No spending or savings contributions in this period." /></div></div></Card>
          </div>
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between gap-2"><div className="flex items-center gap-2"><MyPiggyIcon name="savings-goal" size="xs" /><h3 className="text-sm font-extrabold">Savings Progress</h3><InfoTooltip compact label="About savings progress">Current balances for active goals, regardless of the report period. Added to Goals above measures contributions during the selected dates.</InfoTooltip></div><span className="text-xs text-gray-600 dark:text-slate-300">{visibleGoals.length} active</span></div>
            {visibleGoals.length === 0 ? <p className="text-sm text-gray-600 dark:text-slate-300">No active savings goals.</p> : <div className="grid items-start gap-3 sm:grid-cols-2">{visibleGoals.map((goal) => {
              const targetCents = Number(goal.target_amount_cents) || 0;
              const percent = targetCents > 0 ? Math.min(100, Math.round(goal.balanceCents / targetCents * 100)) : 0;
              return <div key={goal.id} className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-slate-700 dark:bg-slate-900/45"><div className="flex items-start justify-between gap-2"><div className="min-w-0"><h4 className="break-words text-sm font-bold">{goal.name}</h4>{childFilter === 'all' && <span className="text-xs font-semibold text-teal-800 dark:text-teal-200">{goal.childName}</span>}</div><span className="shrink-0 text-xs font-extrabold">{percent}%</span></div><p className="mt-1 text-xs text-gray-600 dark:text-slate-300"><strong>{formatCurrency(goal.balanceCents)}</strong> of {formatCurrency(targetCents)}</p><div role="progressbar" aria-label={goal.name} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700"><div className="h-full rounded-full bg-teal-600 dark:bg-teal-400" style={{ width: `${percent}%` }} /></div></div>;
            })}</div>}
          </Card>
          <Card className="p-4">
            <div className="mb-3 flex items-center gap-2"><h3 className="text-sm font-extrabold">Money Habits</h3><InfoTooltip compact label="About Money Habits">Observations for the selected child and dates, not grades or sibling comparisons. Savings contributions can exceed 100% of Money Added when children save money they already had.</InfoTooltip></div>
            <div className="grid items-start gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-950/20"><div className="flex items-center justify-between gap-2"><h4 className="text-xs font-extrabold">Savings Habit</h4><InfoTooltip compact label="About Savings Habit">{moneyHabitCopy.savingsText}</InfoTooltip></div><p className="mt-1 text-lg font-extrabold text-emerald-800 dark:text-emerald-200">{moneyHabits.savingsPercent === null ? '—' : `${moneyHabits.savingsPercent}%`}</p><p className="text-xs text-gray-600 dark:text-slate-300">{moneyHabits.savingsPercent === null ? 'No money added this period.' : 'Of Money Added moved to goals.'}</p></div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/20"><div className="flex items-center justify-between gap-2"><h4 className="text-xs font-extrabold">Chore Earnings</h4><InfoTooltip compact label="About Chore Earnings habit">{moneyHabitCopy.choreText}</InfoTooltip></div><p className="mt-1 text-lg font-extrabold text-amber-800 dark:text-amber-200">{formatCurrency(moneyHabits.choreEarningsCents)}</p><p className="text-xs text-gray-600 dark:text-slate-300">{moneyHabits.approvedChoreCount} approved {moneyHabits.approvedChoreCount === 1 ? 'chore' : 'chores'} this period.</p></div>
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950/20"><div className="flex items-center justify-between gap-2"><h4 className="text-xs font-extrabold">Goal Progress</h4><InfoTooltip compact label="About Goal Progress habit">{moneyHabitCopy.goalText}</InfoTooltip></div><p className="mt-1 text-lg font-extrabold text-blue-800 dark:text-blue-200">{moneyHabits.contributedGoalCount}/{moneyHabits.activeGoalCount}</p><p className="text-xs text-gray-600 dark:text-slate-300">{moneyHabits.activeGoalCount ? 'Active goals received money this period.' : 'No active goals right now.'}</p></div>
            </div>
          </Card>
          <Card className="p-4 border-teal-200 dark:border-teal-800">
            <div className="flex flex-wrap items-center justify-between gap-3"><div className="flex min-w-0 items-start gap-2"><MyPiggyIcon name="transaction-history" size="sm" /><div><h3 className="text-sm font-extrabold">Explore the Activity Behind This Report</h3><p className="mt-1 text-xs text-gray-600 dark:text-slate-300">View {reportActivity.length} matching {reportActivity.length === 1 ? 'activity' : 'activities'}, full details and CSV export with the same child and report dates.</p></div></div><button type="button" onClick={viewTransactions} className="min-h-11 rounded-lg bg-teal-700 px-4 py-2 text-sm font-bold text-white hover:bg-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-500">View All Transactions →</button></div>
          </Card>
        </>}
      </div>
    </AppLayout>
  );
}
